import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useParams, useLocation } from 'react-router-dom';

// ComingSoonPage se importa EAGER a propósito: es la puerta de lanzamiento que
// ve el público sin sesión, así que debe estar en el bundle inicial (mínimo)
// para pintar de inmediato. Todo lo demás va lazy (code-splitting).
import { ComingSoonPage } from '@/routes/public/ComingSoonPage';

// Guards + layout + hooks: eager, son livianos y estructuran el árbol de rutas.
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { AuthGuard } from '@/components/layout/AuthGuard';
import { GuruGuard } from '@/components/layout/GuruGuard';
import { AdminGuard, AdminRouteGate } from '@/components/layout/AdminGuard';
import { ClientGuard } from '@/components/layout/ClientGuard';
import { SubscriptionGuard } from '@/components/layout/SubscriptionGuard';
import { MembersOnlyGate } from '@/components/domain/MembersOnlyGate';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import { useAuthListener, useSession } from '@/lib/auth';
import { GuruLegalGate } from '@/components/layout/GuruLegalGate';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { installErrorReporting } from '@/lib/error-reporting';
import { esErrorDeChunk, huellaEnMemoria, marcarArranqueSano, recargarPorDeploy, vigilarVersion } from '@/lib/app-version';
import { registerBuild } from '@/lib/analytics';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { useAnalyticsIdentify } from '@/lib/hooks/useAnalyticsIdentify';
import { useLaunchMode } from '@/lib/api/launch';
import { metaPageView } from '@/lib/meta-pixel';

// Helper: React.lazy espera un módulo con export default; nuestras páginas usan
// export nombrado, así que envolvemos para exponer { default: Componente }.
//
// Además atrapa el import que falla por deploy vencido. Cuando publicamos, los
// archivos con hash del build anterior dejan de existir; quien ya estaba adentro
// pide uno de esos y la app se cae. En vez de mostrarle un error por algo que no
// es suyo, recargamos con un cartel de "actualizando". La guarda anti-bucle vive
// en `recargarPorDeploy`: si no recarga, dejamos que el error siga su curso y lo
// atrape el ErrorBoundary, porque entonces sí es un problema nuestro.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyNamed(factory: () => Promise<any>, name: string) {
  return lazy(async () => {
    try {
      return { default: (await factory())[name] };
    } catch (err) {
      // `recargarPorDeploy` va a comprobar contra el servidor si de verdad
      // hay una versión nueva antes de decidir. Si no la hay (sin red, CDN
      // caído, bug nuestro), devuelve false y el error sigue su curso.
      if (esErrorDeChunk(err) && (await recargarPorDeploy())) {
        // La página se está yendo. Una promesa que nunca resuelve deja el
        // fallback de Suspense en pantalla en vez de pintar un error a medias.
        return await new Promise<never>(() => {});
      }
      throw err;
    }
  });
}

// ── Públicas ─────────────────────────────────────────────────────────
const LandingPage = lazyNamed(() => import('@/routes/public/LandingPage'), 'LandingPage');
const PricingPage = lazyNamed(() => import('@/routes/public/PricingPage'), 'PricingPage');
const LoginPage = lazyNamed(() => import('@/routes/public/LoginPage'), 'LoginPage');
const RegisterPage = lazyNamed(() => import('@/routes/public/RegisterPage'), 'RegisterPage');
const GuruRegisterPage = lazyNamed(() => import('@/routes/public/GuruRegisterPage'), 'GuruRegisterPage');
const AuthCallbackPage = lazyNamed(() => import('@/routes/public/AuthCallbackPage'), 'AuthCallbackPage');
const ForgotPasswordPage = lazyNamed(() => import('@/routes/public/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyNamed(() => import('@/routes/public/ResetPasswordPage'), 'ResetPasswordPage');
const MentorsDirectoryPage = lazyNamed(() => import('@/routes/public/MentorsDirectoryPage'), 'MentorsDirectoryPage');
const MentorProfilePage = lazyNamed(() => import('@/routes/public/MentorProfilePage'), 'MentorProfilePage');
const FoundersPage = lazyNamed(() => import('@/routes/public/FoundersPage'), 'FoundersPage');
const TermsGuruPage = lazyNamed(() => import('@/routes/public/TermsGuruPage'), 'TermsGuruPage');
const MentorAgreementPage = lazyNamed(() => import('@/routes/public/MentorAgreementPage'), 'MentorAgreementPage');
const PrivacyPolicyPage = lazyNamed(() => import('@/routes/public/PrivacyPolicyPage'), 'PrivacyPolicyPage');
const TermsOfServicePage = lazyNamed(() => import('@/routes/public/TermsOfServicePage'), 'TermsOfServicePage');
const CancellationPolicyPage = lazyNamed(() => import('@/routes/public/CancellationPolicyPage'), 'CancellationPolicyPage');
const CertificateVerifyPage = lazyNamed(() => import('@/routes/public/CertificateVerifyPage'), 'CertificateVerifyPage');
const CertificateValidatorPage = lazyNamed(() => import('@/routes/public/CertificateValidatorPage'), 'CertificateValidatorPage');
const ConfirmEmailPage = lazyNamed(() => import('@/routes/public/EmailLinkPages'), 'ConfirmEmailPage');
const UnsubscribePage = lazyNamed(() => import('@/routes/public/EmailLinkPages'), 'UnsubscribePage');
const VendorPortalPage = lazyNamed(() => import('@/routes/public/VendorPortalPage'), 'VendorPortalPage');
const PresentationReviewPage = lazyNamed(() => import('@/routes/public/PresentationReviewPage'), 'PresentationReviewPage');
const CoursesPublicPage = lazyNamed(() => import('@/routes/public/CoursesPublicPage'), 'CoursesPublicPage');
const CoursePublicDetailPage = lazyNamed(() => import('@/routes/public/CoursePublicDetailPage'), 'CoursePublicDetailPage');
const PathPublicDetailPage = lazyNamed(() => import('@/routes/public/PathPublicDetailPage'), 'PathPublicDetailPage');
const RadarPublicPage = lazyNamed(() => import('@/routes/public/RadarPublicPage'), 'RadarPublicPage');
const RadarTrendPublicPage = lazyNamed(() => import('@/routes/public/RadarTrendPublicPage'), 'RadarTrendPublicPage');
const EventsPublicPage = lazyNamed(() => import('@/routes/public/EventsPublicPage'), 'EventsPublicPage');
const EventPublicDetailPage = lazyNamed(() => import('@/routes/public/EventPublicDetailPage'), 'EventPublicDetailPage');
const TeamsPage = lazyNamed(() => import('@/routes/public/TeamsPage'), 'TeamsPage');
const ContactPage = lazyNamed(() => import('@/routes/public/ContactPage'), 'ContactPage');
const LearnAndEarnPage = lazyNamed(() => import('@/routes/public/LearnAndEarnPage'), 'LearnAndEarnPage');
const LinksPage = lazyNamed(() => import('@/routes/public/LinksPage'), 'LinksPage');
const BeAGuruPage = lazyNamed(() => import('@/routes/public/BeAGuruPage'), 'BeAGuruPage');
const InvitacionGuruPage = lazyNamed(() => import('@/routes/public/InvitacionGuruPage'), 'InvitacionGuruPage');
const InvitacionCursoEnVivoPage = lazyNamed(() => import('@/routes/public/InvitacionCursoEnVivoPage'), 'InvitacionCursoEnVivoPage');
const LiveCoursesPublicPage = lazyNamed(() => import('@/routes/public/LiveCoursesPublicPage'), 'LiveCoursesPublicPage');
const LiveCoursePublicPage = lazyNamed(() => import('@/routes/public/LiveCoursePublicPage'), 'LiveCoursePublicPage');
const LiveCoursesDashboardPage = lazyNamed(() => import('@/routes/dashboard/LiveCoursesDashboardPage'), 'LiveCoursesDashboardPage');
const LiveCourseDashboardPage = lazyNamed(() => import('@/routes/dashboard/LiveCourseDashboardPage'), 'LiveCourseDashboardPage');
const PresentationViewerPage = lazyNamed(() => import('@/routes/presentacion/PresentationViewerPage'), 'PresentationViewerPage');

// ── Dashboard (cliente) ──────────────────────────────────────────────
const DashboardHome = lazyNamed(() => import('@/routes/dashboard/DashboardHome'), 'DashboardHome');
const CoursesCatalogPage = lazyNamed(() => import('@/routes/dashboard/CoursesCatalogPage'), 'CoursesCatalogPage');
const CourseDetailPage = lazyNamed(() => import('@/routes/dashboard/CourseDetailPage'), 'CourseDetailPage');
const CoursePurchasedPage = lazyNamed(() => import('@/routes/dashboard/CoursePurchasedPage'), 'CoursePurchasedPage');
const FavoritesPage = lazyNamed(() => import('@/routes/dashboard/FavoritesPage'), 'FavoritesPage');
const MyCoursesPage = lazyNamed(() => import('@/routes/dashboard/MyCoursesPage'), 'MyCoursesPage');
const AchievementsPage = lazyNamed(() => import('@/routes/dashboard/AchievementsPage'), 'AchievementsPage');
const ReferralsPage = lazyNamed(() => import('@/routes/dashboard/ReferralsPage'), 'ReferralsPage');
const LearningPathDetailPage = lazyNamed(() => import('@/routes/dashboard/LearningPathDetailPage'), 'LearningPathDetailPage');
const LearningPathsListPage = lazyNamed(() => import('@/routes/dashboard/LearningPathsListPage'), 'LearningPathsListPage');
const MyCourseHistoryDetailPage = lazyNamed(() => import('@/routes/dashboard/MyCourseHistoryDetailPage'), 'MyCourseHistoryDetailPage');
const LessonPlayerPage = lazyNamed(() => import('@/routes/dashboard/LessonPlayerPage'), 'LessonPlayerPage');
const QuizDetailPage = lazyNamed(() => import('@/routes/dashboard/QuizDetailPage'), 'QuizDetailPage');
const QuizAttemptPage = lazyNamed(() => import('@/routes/dashboard/QuizAttemptPage'), 'QuizAttemptPage');
const QuizResultPage = lazyNamed(() => import('@/routes/dashboard/QuizResultPage'), 'QuizResultPage');
const CourseCompletedPage = lazyNamed(() => import('@/routes/dashboard/CourseCompletedPage'), 'CourseCompletedPage');
const AssignmentDetailPage = lazyNamed(() => import('@/routes/dashboard/AssignmentDetailPage'), 'AssignmentDetailPage');
const BookingFlowPage = lazyNamed(() => import('@/routes/dashboard/BookingFlowPage'), 'BookingFlowPage');
const BookingReviewPage = lazyNamed(() => import('@/routes/dashboard/BookingReviewPage'), 'BookingReviewPage');
const MySessionsPage = lazyNamed(() => import('@/routes/dashboard/MySessionsPage'), 'MySessionsPage');
const BookingPaidPage = lazyNamed(() => import('@/routes/dashboard/BookingPaidPage'), 'BookingPaidPage');
const NotificationsPage = lazyNamed(() => import('@/routes/dashboard/NotificationsPage'), 'NotificationsPage');
const AccountPage = lazyNamed(() => import('@/routes/dashboard/AccountPage'), 'AccountPage');
const CommunityFeedPage = lazyNamed(() => import('@/routes/dashboard/CommunityFeedPage'), 'CommunityFeedPage');
const CommunityPostPage = lazyNamed(() => import('@/routes/dashboard/CommunityPostPage'), 'CommunityPostPage');
const InternalMentorsPage = lazyNamed(() => import('@/routes/dashboard/InternalMentorsPage'), 'InternalMentorsPage');
const InternalMentorProfilePage = lazyNamed(() => import('@/routes/dashboard/InternalMentorProfilePage'), 'InternalMentorProfilePage');
const EventsListPage = lazyNamed(() => import('@/routes/dashboard/EventsListPage'), 'EventsListPage');
const EventDetailPage = lazyNamed(() => import('@/routes/dashboard/EventDetailPage'), 'EventDetailPage');
const MyTicketsPage = lazyNamed(() => import('@/routes/dashboard/MyTicketsPage'), 'MyTicketsPage');
const NewTicketPage = lazyNamed(() => import('@/routes/dashboard/NewTicketPage'), 'NewTicketPage');
const TicketDetailPage = lazyNamed(() => import('@/routes/dashboard/TicketDetailPage'), 'TicketDetailPage');

// ── Guru ─────────────────────────────────────────────────────────────
const GuruDashboard = lazyNamed(() => import('@/routes/guru/GuruDashboard'), 'GuruDashboard');
const GuruAvailabilityPage = lazyNamed(() => import('@/routes/guru/GuruAvailabilityPage'), 'GuruAvailabilityPage');
const GradingPage = lazyNamed(() => import('@/routes/guru/GradingPage'), 'GradingPage');
const GuruSettlementsPage = lazyNamed(() => import('@/routes/guru/GuruSettlementsPage'), 'GuruSettlementsPage');
const GuruOnboardingPage = lazyNamed(() => import('@/routes/guru/GuruOnboardingPage'), 'GuruOnboardingPage');
const GuruCoursesPage = lazyNamed(() => import('@/routes/guru/GuruCoursesPage'), 'GuruCoursesPage');
const GuruCourseNewPage = lazyNamed(() => import('@/routes/guru/GuruCourseNewPage'), 'GuruCourseNewPage');
const GuruCourseEditPage = lazyNamed(() => import('@/routes/guru/GuruCourseEditPage'), 'GuruCourseEditPage');
const GuruLiveCoursePage = lazyNamed(() => import('@/routes/guru/GuruLiveCoursePage'), 'GuruLiveCoursePage');
const GuruProposeCoursePage = lazyNamed(() => import('@/routes/guru/GuruProposeCoursePage'), 'GuruProposeCoursePage');

// ── Admin ────────────────────────────────────────────────────────────
const AdminHome = lazyNamed(() => import('@/routes/admin/AdminHome'), 'AdminHome');
const AdminTractionPage = lazyNamed(() => import('@/routes/admin/AdminTractionPage'), 'AdminTractionPage');
const AdminFinancePage = lazyNamed(() => import('@/routes/admin/AdminFinancePage'), 'AdminFinancePage');
const AdminInvitationsPage = lazyNamed(() => import('@/routes/admin/AdminInvitationsPage'), 'AdminInvitationsPage');
const AdminLeadsPage = lazyNamed(() => import('@/routes/admin/AdminLeadsPage'), 'AdminLeadsPage');
const AdminMentorsPage = lazyNamed(() => import('@/routes/admin/AdminMentorsPage'), 'AdminMentorsPage');
const AdminAvatarReviewPage = lazyNamed(() => import('@/routes/admin/AdminAvatarReviewPage'), 'AdminAvatarReviewPage');
const AdminFoundersPage = lazyNamed(() => import('@/routes/admin/AdminFoundersPage'), 'AdminFoundersPage');
const AdminEventsPage = lazyNamed(() => import('@/routes/admin/AdminEventsPage'), 'AdminEventsPage');
const AdminLiveCoursesPage = lazyNamed(() => import('@/routes/admin/AdminLiveCoursesPage'), 'AdminLiveCoursesPage');
const AdminPaymentsPage = lazyNamed(() => import('@/routes/admin/AdminPaymentsPage'), 'AdminPaymentsPage');
const AdminTaxReviewPage = lazyNamed(() => import('@/routes/admin/AdminTaxReviewPage'), 'AdminTaxReviewPage');
const AdminVendorsPage = lazyNamed(() => import('@/routes/admin/AdminVendorsPage'), 'AdminVendorsPage');
const AdminLegalPage = lazyNamed(() => import('@/routes/admin/AdminLegalPage'), 'AdminLegalPage');
const AdminLegalEditorPage = lazyNamed(() => import('@/routes/admin/AdminLegalEditorPage'), 'AdminLegalEditorPage');
const AdminWebhookFailuresPage = lazyNamed(() => import('@/routes/admin/AdminWebhookFailuresPage'), 'AdminWebhookFailuresPage');
const AdminAuditPage = lazyNamed(() => import('@/routes/admin/AdminAuditPage'), 'AdminAuditPage');
const AdminSecurityPage = lazyNamed(() => import('@/routes/admin/AdminSecurityPage'), 'AdminSecurityPage');
const AdminCommunityModerationPage = lazyNamed(() => import('@/routes/admin/AdminCommunityModerationPage'), 'AdminCommunityModerationPage');
const AdminRadarSocialPage = lazyNamed(() => import('@/routes/admin/AdminRadarSocialPage'), 'AdminRadarSocialPage');
const AdminCoursesPage = lazyNamed(() => import('@/routes/admin/AdminCoursesPage'), 'AdminCoursesPage');
const AdminCourseNewPage = lazyNamed(() => import('@/routes/admin/AdminCourseNewPage'), 'AdminCourseNewPage');
const AdminCoursesReviewPage = lazyNamed(() => import('@/routes/admin/AdminCoursesReviewPage'), 'AdminCoursesReviewPage');
const AdminCategoriesPage = lazyNamed(() => import('@/routes/admin/AdminCategoriesPage'), 'AdminCategoriesPage');
const AdminCourseEditPage = lazyNamed(() => import('@/routes/admin/AdminCourseEditPage'), 'AdminCourseEditPage');
const AdminAchievementsPage = lazyNamed(() => import('@/routes/admin/AdminAchievementsPage'), 'AdminAchievementsPage');
const AdminGamificationPage = lazyNamed(() => import('@/routes/admin/AdminGamificationPage'), 'AdminGamificationPage');
const AdminUsersPage = lazyNamed(() => import('@/routes/admin/AdminUsersPage'), 'AdminUsersPage');
const AdminTicketsPage = lazyNamed(() => import('@/routes/admin/AdminTicketsPage'), 'AdminTicketsPage');
const AdminTicketCategoriesPage = lazyNamed(() => import('@/routes/admin/AdminTicketCategoriesPage'), 'AdminTicketCategoriesPage');
const AdminTicketDetailPage = lazyNamed(() => import('@/routes/admin/AdminTicketDetailPage'), 'AdminTicketDetailPage');

// Modo lanzamiento (flag en base de datos, no en el build): cuando está
// encendido, el público (no autenticado) ve la página de expectativa en las
// rutas de marketing. Admin y gurus (con sesión) entran al sitio real;
// login/registro-guru y legales quedan abiertos para los invitados.
const GATED_MARKETING = ['/pricing', '/founders', '/gurus', '/courses', '/radar', '/events', '/teams', '/aprende-y-gana', '/paths'];
function isGatedPath(pathname: string): boolean {
  if (pathname === '/') return true;
  return GATED_MARKETING.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

/** Redirect /mentors → /gurus para no romper links externos / históricos. */
function MentorsRedirect() {
  const { handle } = useParams<{ handle?: string }>();
  return <Navigate to={handle ? `/gurus/${handle}` : '/gurus'} replace />;
}

/** Redirect /signup → /register preservando query string. */
function SignupRedirect() {
  const location = useLocation();
  return <Navigate to={`/register${location.search}`} replace />;
}

/** Fallback mínimo mientras un chunk lazy se descarga. */
function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
    </div>
  );
}

export default function App() {
  useAuthListener();
  useAnalyticsIdentify();

  // Errores asincrónicos: los que el ErrorBoundary no puede ver.
  useEffect(() => { installErrorReporting(); }, []);

  // Qué build está corriendo esta pestaña. Va como super-propiedad, así que
  // cada evento posterior —incluidos los de error— lo lleva estampado y no hay
  // que deducirlo de las horas de los commits.
  useEffect(() => { registerBuild(huellaEnMemoria()); }, []);
  // Amnistía del contador de recargas-por-deploy: si la app corre estable
  // 20s, la última recarga funcionó y el historial deja de contar como bucle.
  useEffect(() => { marcarArranqueSano(); }, []);

  // Aviso de versión nueva. Publicar a media tarde no debería interrumpir a
  // nadie: en vez de esperar a que choque con un archivo que ya no existe, le
  // decimos que hay algo nuevo y que actualice cuando le quede bien. El aviso no
  // se cierra solo — si se pierde, el usuario se queda con la versión vieja y
  // volvemos al problema que estamos resolviendo.
  useEffect(() => vigilarVersion((permitirReaviso) => {
    // Card propia (toast.custom) en vez del toast default de sonner: colores
    // fijos de marca sobre fondo oscuro, así se ve idéntica y legible tanto
    // en las páginas dark del público como en el dashboard claro.
    toast.custom((t) => (
      <div className="pointer-events-auto w-[356px] rounded-2xl border border-white/15 bg-[#0d1f26] p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-brand-accent/15">
            <RefreshCw className="h-[18px] w-[18px] text-brand-accent" />
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-white">Gurumba se actualizó</p>
            <p className="mt-0.5 text-sm leading-snug text-white/65">
              Hay mejoras listas para ti. Recarga cuando quieras: tu sesión y tu
              progreso quedan intactos.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-lg bg-brand-energy px-3.5 py-1.5 text-sm font-semibold text-[#071114] transition-opacity hover:opacity-90"
              >
                Recargar ahora
              </button>
              <button
                type="button"
                onClick={() => toast.dismiss(t)}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-white/60 transition-colors hover:text-white"
              >
                Más tarde
              </button>
            </div>
          </div>
        </div>
      </div>
    ), {
      duration: Infinity,
      // Un swipe en el móvil (o "Más tarde") cierra el aviso. Si eso pasa,
      // volvemos a avisar en la siguiente comprobación en vez de dejar al
      // usuario en la versión vieja hasta que choque con ella.
      onDismiss: permitirReaviso,
    });
  }), []);

  const location = useLocation();
  const { data: session } = useSession();
  const { data: launchMode } = useLaunchMode();

  // Píxel de Meta: un PageView por cada cambio de ruta (el primero lo manda index.html).
  useEffect(() => { metaPageView(); }, [location.pathname]);

  // Puerta de lanzamiento: público sin sesión → coming-soon en marketing.
  // Solo cerramos la puerta cuando el flag está confirmado en true. Con
  // `?? true` el visitante nuevo (sin cache en localStorage) veía un parpadeo
  // de "próximamente" mientras volaba el RPC. Con el sitio ya abierto, el
  // riesgo se invirtió: es peor el parpadeo que un instante de home real.
  if (launchMode === true && !session && isGatedPath(location.pathname)) {
    return <ComingSoonPage />;
  }

  return (
    <ErrorBoundary>
    <ScrollToTop />
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* === Públicas (sin auth) === */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/founders" element={<FoundersPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/guru" element={<GuruRegisterPage />} />
      <Route path="/signup" element={<SignupRedirect />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Rutas públicas de Gurus (antes /mentors — redirect para links viejos) */}
      <Route path="/gurus" element={<MentorsDirectoryPage />} />
      <Route path="/gurus/:handle" element={<MentorProfilePage />} />
      <Route path="/mentors" element={<MentorsRedirect />} />
      <Route path="/mentors/:handle" element={<MentorsRedirect />} />

      {/* Cursos públicos */}
      <Route path="/courses" element={<CoursesPublicPage />} />
      <Route path="/courses/:slug" element={<CoursePublicDetailPage />} />

      {/* Radar Público */}
      <Route path="/radar" element={<RadarPublicPage />} />
      <Route path="/radar/:slug" element={<RadarTrendPublicPage />} />

      {/* Paths públicos */}
      <Route path="/paths/:slug" element={<PathPublicDetailPage />} />

      {/* Eventos públicos (SIEMPRE públicos, incluso con sesión) */}
      <Route path="/events" element={<EventsPublicPage />} />
      <Route path="/events/:slug" element={<EventPublicDetailPage />} />
      {/* Cursos en vivo (2026-10-06): la vitrina y la página de venta de cada
          curso. Públicas y fuera de la cortina: se comparten por redes. */}
      <Route path="/cursos-en-vivo" element={<LiveCoursesPublicPage />} />
      <Route path="/cursos-en-vivo/:slug" element={<LiveCoursePublicPage />} />

      {/* B2B landing + contacto */}
      <Route path="/teams" element={<TeamsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/aprende-y-gana" element={<LearnAndEarnPage />} />

      {/* Bio de Instagram — linktree propio (fuera de la puerta de coming-soon) */}
      <Route path="/links" element={<LinksPage />} />

      {/* Reclutamiento de Gurus */}
      <Route path="/be-a-guru" element={<BeAGuruPage />} />

      {/* Microsite privado de invitación a gurus (?para=Nombre). Fuera de la
          puerta de coming-soon (no está en GATED_MARKETING) y noindex. */}
      <Route path="/invitacion" element={<InvitacionGuruPage />} />
      <Route path="/invitacion/:code" element={<InvitacionGuruPage />} />
      {/* Invitación a los gurus del club a hacer su curso en vivo: cómo
          funciona, calculadora y botón para empezar. Va en el correo
          send-live-course-invite. También noindex y fuera de la cortina. */}
      <Route path="/invitacion/curso-en-vivo" element={<InvitacionCursoEnVivoPage />} />

      {/* Legales públicos (indexables) */}
      <Route path="/politica-privacidad" element={<PrivacyPolicyPage />} />
      <Route path="/terminos-del-servicio" element={<TermsOfServicePage />} />
      <Route path="/politica-cancelacion" element={<CancellationPolicyPage />} />
      <Route path="/certificado" element={<CertificateValidatorPage />} />
      {/* Links de los correos (sin sesión): confirmar correo y darse de baja. */}
      <Route path="/correo/confirmar" element={<ConfirmEmailPage />} />
      <Route path="/correo/baja" element={<UnsubscribePage />} />
      {/* Portal del proveedor (2026-10-26): sin cuenta, el token del link es
          la llave. Fuera de la cortina y noindex. */}
      <Route path="/proveedor/:token" element={<VendorPortalPage />} />
      {/* Revisión privada de una presentación (2026-10-27): alguien que el
          guru invita, sin cuenta; el token del link es la llave y queda
          amarrado a un navegador. Fuera de la cortina y noindex. */}
      <Route path="/revisar/:token" element={<PresentationReviewPage />} />
      <Route path="/certificado/:code" element={<CertificateVerifyPage />} />

      {/* Legales para Mentores (accesibles por URL pero noindex) */}
      {/* Documentos de la relación Gadamax LLC ↔ Guru. No son públicos:
          pasa quien tenga sesión o venga del registro con invitación validada. */}
      <Route path="/terms-guru" element={<GuruLegalGate><TermsGuruPage /></GuruLegalGate>} />
      <Route path="/mentor-agreement" element={<GuruLegalGate><MentorAgreementPage /></GuruLegalGate>} />

      {/* === Cliente: shell del dashboard === */}
      <Route
        element={
          <AuthGuard>
            <ClientGuard>
              <SubscriptionGuard>
                <DashboardLayout />
              </SubscriptionGuard>
            </ClientGuard>
          </AuthGuard>
        }
      >
        <Route path="/dashboard" element={<DashboardHome />} />
        <Route path="/dashboard/courses" element={<CoursesCatalogPage />} />
        <Route path="/dashboard/favorites" element={<FavoritesPage />} />
        <Route path="/dashboard/my-courses" element={<MyCoursesPage />} />
        <Route path="/dashboard/my-courses/:slug" element={<MyCourseHistoryDetailPage />} />
        <Route path="/dashboard/courses/:slug" element={<CourseDetailPage />} />
        <Route path="/courses/:slug/purchased" element={<CoursePurchasedPage />} />
        <Route path="/courses/:slug/completed" element={<CourseCompletedPage />} />
        <Route path="/courses/:slug/quizzes/:quizId" element={<QuizDetailPage />} />
        <Route path="/courses/:slug/quizzes/:quizId/attempt/:attemptId" element={<QuizAttemptPage />} />
        <Route path="/courses/:slug/quizzes/:quizId/result/:attemptId" element={<QuizResultPage />} />
        <Route path="/courses/:slug/assignments/:assignmentId" element={<AssignmentDetailPage />} />
        <Route path="/dashboard/notifications" element={<NotificationsPage />} />
        <Route path="/dashboard/achievements" element={<MembersOnlyGate feature="achievements"><AchievementsPage /></MembersOnlyGate>} />
        <Route path="/dashboard/referrals" element={<MembersOnlyGate feature="referrals"><ReferralsPage /></MembersOnlyGate>} />
        <Route path="/dashboard/paths" element={<LearningPathsListPage />} />
        <Route path="/dashboard/paths/:slug" element={<LearningPathDetailPage />} />
      </Route>

      {/* Presentación de un curso en vivo: privada (guru del curso y admin,
          con sesión; lo controla RLS del bucket). Pantalla completa, sin shell. */}
      <Route
        path="/presentacion/:courseId"
        element={<AuthGuard><PresentationViewerPage /></AuthGuard>}
      />

      {/* Reproductor lección — layout inmersivo */}
      <Route
        path="/courses/:slug/lessons/:lessonId"
        element={
          <AuthGuard>
            <ClientGuard>
              <SubscriptionGuard>
                <LessonPlayerPage />
              </SubscriptionGuard>
            </ClientGuard>
          </AuthGuard>
        }
      />

      {/* === Guru === */}
      <Route
        element={
          <AuthGuard>
            <GuruGuard>
              <DashboardLayout />
            </GuruGuard>
          </AuthGuard>
        }
      >
        <Route path="/guru" element={<GuruDashboard />} />
        <Route path="/guru/sessions" element={<MySessionsPage />} />
        <Route path="/guru/availability" element={<GuruAvailabilityPage />} />
        <Route path="/guru/grading" element={<GradingPage />} />
        <Route path="/guru/courses" element={<GuruCoursesPage />} />
        <Route path="/guru/courses/proponer" element={<GuruProposeCoursePage />} />
        <Route path="/guru/courses/new" element={<GuruCourseNewPage />} />
        <Route path="/guru/courses/:id/edit" element={<GuruCourseEditPage />} />
        <Route path="/guru/curso-en-vivo" element={<GuruLiveCoursePage />} />
        <Route path="/guru/settlements" element={<GuruSettlementsPage />} />
        <Route path="/guru/onboarding" element={<GuruOnboardingPage />} />
        <Route path="/guru/notifications" element={<NotificationsPage />} />
      </Route>

      {/* === Comunidad + Eventos (gateados por plan) === */}
      <Route
        element={
          <AuthGuard>
            <SubscriptionGuard>
              <DashboardLayout />
            </SubscriptionGuard>
          </AuthGuard>
        }
      >
        <Route path="/community" element={<MembersOnlyGate feature="community"><CommunityFeedPage /></MembersOnlyGate>} />
        <Route path="/community/posts/:postId" element={<MembersOnlyGate feature="community"><CommunityPostPage /></MembersOnlyGate>} />
        <Route path="/dashboard/events" element={<EventsListPage />} />
        <Route path="/dashboard/events/:slug" element={<EventDetailPage />} />
      </Route>

      {/* === Circuito de sesión suelta (pay-per-session) ===
          Con login pero SIN SubscriptionGuard: la promesa pública dice
          "Reserva una sesión 1:1 sin suscripción" y el backend
          (create-session-checkout) nunca exigió plan — cobra la tarifa del
          guru y ya. Antes estas rutas vivían dentro del guard y rebotaban a
          /pricing a todo cliente sin plan: la promesa era incumplible.
          La parrilla de gurus (/dashboard/mentors) también vive acá: es la
          vitrina del circuito de sesión suelta — el cliente sin plan debe
          poder ver y elegir gurus desde su cuenta. El resto del dashboard
          (cursos, comunidad, eventos) sigue detrás del guard, que ahora
          upsellea adentro (/account?tab=suscripcion), no en el sitio público. */}
      <Route
        element={
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        }
      >
        <Route path="/dashboard/mentors" element={<InternalMentorsPage />} />
        {/* Cursos en vivo dentro del panel: los ve y los compra cualquiera con
            sesión (la entrada no pide plan), igual que las sesiones 1:1. */}
        <Route path="/dashboard/cursos-en-vivo" element={<LiveCoursesDashboardPage />} />
        <Route path="/dashboard/cursos-en-vivo/:slug" element={<LiveCourseDashboardPage />} />
        <Route path="/dashboard/mentors/:handle" element={<InternalMentorProfilePage />} />
        <Route path="/dashboard/mentors/:handle/book" element={<BookingFlowPage />} />
        <Route path="/dashboard/bookings/:bookingId/review" element={<BookingReviewPage />} />
        <Route path="/dashboard/sessions" element={<MySessionsPage />} />
        <Route path="/dashboard/sessions/paid" element={<BookingPaidPage />} />
      </Route>

      {/* === Account + Tickets: válvula de escape === */}
      <Route
        element={
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        }
      >
        <Route path="/account" element={<AccountPage />} />
        <Route path="/account/tickets" element={<MyTicketsPage />} />
        <Route path="/account/tickets/new" element={<NewTicketPage />} />
        <Route path="/account/tickets/:ticketId" element={<TicketDetailPage />} />
      </Route>

      {/* === Admin === */}
      <Route
        element={
          <AuthGuard>
            <AdminGuard>
              {/* Cada ruta /admin/* exige su permiso (socios, editores…). */}
              <AdminRouteGate>
                <DashboardLayout />
              </AdminRouteGate>
            </AdminGuard>
          </AuthGuard>
        }
      >
        <Route path="/admin" element={<AdminHome />} />
        <Route path="/admin/traccion" element={<AdminTractionPage />} />
        <Route path="/admin/finanzas" element={<AdminFinancePage />} />
        <Route path="/admin/invitations" element={<AdminInvitationsPage />} />
        <Route path="/admin/leads" element={<AdminLeadsPage />} />
        <Route path="/admin/mentors" element={<AdminMentorsPage />} />
        <Route path="/admin/fotos" element={<AdminAvatarReviewPage />} />
        <Route path="/admin/founders" element={<AdminFoundersPage />} />
        <Route path="/admin/events" element={<AdminEventsPage />} />
        <Route path="/admin/cursos-en-vivo" element={<AdminLiveCoursesPage />} />
        <Route path="/admin/payments" element={<AdminPaymentsPage />} />
        <Route path="/admin/tax-review" element={<AdminTaxReviewPage />} />
        <Route path="/admin/proveedores" element={<AdminVendorsPage />} />
        <Route path="/admin/legal" element={<AdminLegalPage />} />
        <Route path="/admin/legal/:documentType" element={<AdminLegalEditorPage />} />
        <Route path="/admin/webhook-failures" element={<AdminWebhookFailuresPage />} />
        <Route path="/admin/audit" element={<AdminAuditPage />} />
        <Route path="/admin/security" element={<AdminSecurityPage />} />
        <Route path="/admin/community-moderation" element={<AdminCommunityModerationPage />} />
        <Route path="/admin/radar-social" element={<AdminRadarSocialPage />} />
        <Route path="/admin/courses" element={<AdminCoursesPage />} />
        <Route path="/admin/courses/new" element={<AdminCourseNewPage />} />
        <Route path="/admin/courses/review" element={<AdminCoursesReviewPage />} />
        <Route path="/admin/courses/categories" element={<AdminCategoriesPage />} />
        <Route path="/admin/courses/:id/edit" element={<AdminCourseEditPage />} />
        <Route path="/admin/achievements" element={<AdminAchievementsPage />} />
        <Route path="/admin/gamification" element={<AdminGamificationPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/tickets" element={<AdminTicketsPage />} />
        <Route path="/admin/tickets/categories" element={<AdminTicketCategoriesPage />} />
        <Route path="/admin/tickets/:ticketId" element={<AdminTicketDetailPage />} />
        <Route path="/admin/notifications" element={<NotificationsPage />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-5xl font-bold text-[#10232B]">404</h1>
        <p className="mt-2 text-[#526978]">Esta ruta aún no existe.</p>
        <Link to="/" className="mt-4 inline-block text-sm text-brand-accent hover:underline">
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}
